import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/content/ContentProvider";
import { Reveal } from "./Reveal";
import { AUDIT, SMALL } from "@/lib/check";

// Every price in one place. The check, the small tool and the Audit are set in
// lib/check.js; the tool and Care come from the published site copy, so this
// list can never disagree with the contact page.
export function useOffers() {
  const { SERVICES } = useContent();
  const [, tool, care] = SERVICES;
  const price = (service) => service.short.split(" · ")[0];
  return [
    {
      key: "check",
      name: "The check",
      price: "Free",
      time: "Two minutes",
      whom: "Any job your team repeats",
      holds: [
        "The hours it costs each month",
        "Where it stalls",
        "A straight answer on what to do next",
      ],
    },
    {
      key: "small",
      name: "A small tool",
      price: SMALL.price,
      time: SMALL.time,
      whom: "A job under ten hours a month",
      holds: [
        "One tool for that one job",
        "It reads, prepares and waits for your approval",
        "You watch it do a real case first",
      ],
    },
    {
      key: "audit",
      name: "The Audit",
      price: AUDIT.price,
      time: AUDIT.time,
      whom: "A job of ten hours a month or more",
      holds: [
        "The job, written down step by step",
        "A ledger of where the hours go",
        "What your software already allows",
        "A fixed price for the tool",
        "A first version doing one real case, while you watch",
        "A list of what you can fix without us",
      ],
    },
    {
      key: "tool",
      name: "The tool",
      price: price(tool),
      time: "Fixed in your Audit",
      whom: "After the Audit, if the answer is yes",
      holds: tool.points,
    },
    {
      key: "care",
      name: "Care",
      price: price(care).replace(" a month", ""),
      time: "A month, optional",
      whom: "If you want us to keep it running",
      holds: care.points,
    },
  ];
}

// The prices on the home page: four numbers and one door to the rest.
export function PriceStrip() {
  const offers = useOffers().filter((o) => o.key !== "care");
  return (
    <section className="bg-white px-5 py-20 lg:py-32" data-testid="price-strip">
      <div className="mx-auto max-w-[1400px]">
        <Reveal className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="nm-title max-w-[800px] text-[40px] leading-[1] sm:text-[72px]">
            Every price is fixed.
          </h2>
          <Link
            to="/pricing"
            data-testid="strip-to-pricing"
            className="group inline-flex w-fit items-center gap-3 text-[15px] font-medium"
          >
            What each one buys
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black text-white transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
              <ArrowUpRight size={15} />
            </span>
          </Link>
        </Reveal>
        <dl className="mt-10 grid gap-3 sm:grid-cols-2 lg:mt-14 lg:grid-cols-4">
          {offers.map((o, i) => (
            <Reveal
              key={o.key}
              delay={i * 80}
              className={`flex min-h-[220px] flex-col justify-between rounded-[28px] p-7 ${o.key === "audit" ? "bg-black text-white" : "bg-[#f7f7f7]"}`}
            >
              <dt>
                <span className="block text-[18px] tracking-[-0.015em]">
                  {o.name}
                </span>
                <span
                  className={`mt-1 block text-[13px] leading-[1.35] ${o.key === "audit" ? "text-white/55" : "opacity-45"}`}
                >
                  {o.whom}
                </span>
              </dt>
              <dd className="mt-8">
                <span className="nm-display block text-[38px] leading-none tracking-[-0.03em] sm:text-[44px]">
                  {o.price}
                </span>
                <span
                  className={`mt-2 block text-[13px] ${o.key === "audit" ? "text-white/55" : "opacity-45"}`}
                >
                  {o.time}
                </span>
              </dd>
            </Reveal>
          ))}
        </dl>
      </div>
    </section>
  );
}
